import React, { JSX, useEffect } from 'react';
import { ComponentProps } from 'lib/component-props';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import { Other } from '.generated/Feature.CW.model';
import { guidsAreEqual } from 'lib/utils/string-utils';

// Serialized enum values not included in src/.generated as /Content/$site/Global is not serialized
enum CallRailScriptType {
  MarketingLanding = '367c6ba1-d24d-4a04-8e5d-350b11cb7e06',
  ThankYou = 'BDA631C9-13A4-4BF7-9046-FBADE51F6ADA',
}

type CallRailProps = ComponentProps & Other.CallRailScript;

const CallRailScript = (props: CallRailProps): JSX.Element => {
  const { scriptType } = props.fields || {};
  const scriptDeduplicationId = 'call-rail-id';
  const isMarketing = guidsAreEqual(scriptType?.id, CallRailScriptType.MarketingLanding);
  const isThankYou = guidsAreEqual(scriptType?.id, CallRailScriptType.ThankYou);

  // Add marketing landing script if that type is requested
  useEffect(() => {
    if (!isMarketing || !scriptType) return;
    if (document.getElementById(scriptDeduplicationId)) return;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://cdn.calltrk.com/companies/508306373/3278ea31696e6cb8111c/12/swap.js';
    script.id = scriptDeduplicationId;

    document.body.appendChild(script);

    return () => {
      const existingScript = document.getElementById(scriptDeduplicationId);
      if (existingScript) document.body.removeChild(existingScript);
    };
  }, [isMarketing, scriptType]);

  // Log thank you event if requested. Poll for window.epd to become available
  useEffect(() => {
    if (!isThankYou) return;

    const logThankYouEvent = () => {
      if (window.epd) {
        console.debug('Logging thank you event');
        window.epd('sendG', '6798031907::Form Submission');
        return true;
      }
      return false;
    };

    // If window.epd is immediately available, return without polling
    if (logThankYouEvent()) return;

    const pollingInterval = setInterval(() => {
      if (logThankYouEvent()) {
        // Stop polling once the event has been received
        clearInterval(pollingInterval);
      }
    }, 100);

    return () => clearInterval(pollingInterval);
  }, [isThankYou]);

  if (isMarketing) {
    return (
      <EditingHelpText>Marketing landing CallRail script is added to this page.</EditingHelpText>
    );
  }
  if (isThankYou) {
    return <EditingHelpText>Thank you CallRail script is added to this page.</EditingHelpText>;
  }

  return (
    <EditingHelpText>
      Be sure to set either addMarketingLandingScript or addThankYouScript to true
    </EditingHelpText>
  );
};

export default CallRailScript;
